import React, { useEffect, useRef, useState } from 'react';
import ResourceView from './ResourceTab/ResourceView';
import ResourceRequestView from './ResourceTab/ResourceRequestView';
import { AssetImportDialog } from './ResourceTab/AssetImportDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useLocation, useSearchParams } from 'react-router';
import { useAppSelector, selectIncident } from 'slices/commonSelectors';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { PremiumResourceManagementModal } from 'components/IAP/PremiumResourceManagementModal';
import {
  Ics211DocumentCard,
  Ics238DocumentCard,
} from 'components/IAP/planningView/widgets/DocumentCards/IncidentDocumentCards';
import {
  useCheckInResource,
  useUpdateResourceCheckIn,
  useUpdateResourceStatus,
  useCheckOutResource,
  ResourceInIncident,
} from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  usePremiumResourceManagementFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { registerFocResHubTourApi } from '../focResTour/focResHubTourBridge';
import { FOC_RES_MODEL_TOUR_PARAM } from '../focResTour/focResModelTourSteps';
import { FOC_RES_213RR_TOUR_PARAM } from '../focResTour/focRes213rrTourSteps';
import { FOC_RES_211_TOUR_IAP_PARAM } from '../focResTour/focRes211TourSteps';

type ResourcesProps = {
  forceFullView?: boolean;
  /**
   * Assets only, without the view toggles. For embedding in a form box that is
   * about assets specifically, such as Box 11 of the ICS-201, where Asset
   * Requests and the ICS-211/238 tabs do not belong.
   */
  assetsOnly?: boolean;
};

const Resources: React.FC<ResourcesProps> = ({ forceFullView, assetsOnly }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const focRequirements = useUscgFocRequirementsFlag();
  const premiumResourceManagement = usePremiumResourceManagementFlag();
  const openRequest = searchParams.get('openRequest');
  const wantsModelTour = searchParams.get(FOC_RES_MODEL_TOUR_PARAM) === '1';
  const wants213rrTour = searchParams.get(FOC_RES_213RR_TOUR_PARAM) === '1';
  const wants211Tour = searchParams.get(FOC_RES_211_TOUR_IAP_PARAM) === '1';
  const tourStartedRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState(
    openRequest && premiumResourceManagement
      ? 'request'
      : wants213rrTour
        ? 'request'
        : wants211Tour
          ? 'ics211'
          : 'resource'
  );
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showImportAssets, setShowImportAssets] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [search, setSearch] = useState('');
  const currentIncident = useAppSelector(selectIncident);
  const incidentId = currentIncident?.id;
  const location = useLocation();
  const isHub =
    location.pathname === '/hub' || location.pathname.startsWith('/hub/');
  const showIcsTabs =
    location.pathname === `/iap/${incidentId}/resources` || forceFullView;

  const hasCheckInFields = (data: Partial<ResourceInIncident>): boolean => {
    const checkInFields = [
      'leader_name',
      'personnel_count',
      'order_request_number',
      'state_and_agency',
      'resource_category',
      'resource_kind',
      'resource_type',
      'home_unit_agency',
      'departure_point',
      'departure_datetime',
      'travel_method',
    ];
    return checkInFields.some((field) => field in data);
  };

  const checkInMutation = useCheckInResource();
  const updateCheckInMutation = useUpdateResourceCheckIn();
  const updateStatusMutation = useUpdateResourceStatus();
  const checkOutMutation = useCheckOutResource();

  const handleResourceUpdate = async (
    updatedResource: Partial<ResourceInIncident>
  ) => {
    if (updatedResource.checkin_datetime && !updatedResource.id) {
      await checkInMutation.mutateAsync(updatedResource);
    } else if (
      updatedResource.checkout_datetime &&
      updatedResource.checkout_datetime !== null &&
      updatedResource.id
    ) {
      await checkOutMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id && hasCheckInFields(updatedResource)) {
      await updateCheckInMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id) {
      await updateStatusMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    }
  };

  const handleTabChange = (next: string) => {
    if (next === 'request' && !premiumResourceManagement) {
      setShowUpgradeModal(true);
      return;
    }
    if (
      next === 'request' &&
      !isHub &&
      location.pathname !== `/iap/${incidentId}/resources`
    ) {
      setShowModal(true);
      return;
    }
    setSelectedTab(next);
  };

  useEffect(() => {
    return registerFocResHubTourApi({
      showResources: () => {
        /* Host is already Resources tab / IAP resources route. */
      },
      setResourcesTab: (tab) => {
        if (tab === 'request' || tab === 'resource' || tab === 'ics211') {
          handleTabChange(tab);
        }
      },
    });
    // Intentionally once per mount; handlers close over latest state via setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const wantsHubTour = wantsModelTour || wants213rrTour;
    const wantsIap211 = wants211Tour && !isHub;
    if (!focRequirements || (!wantsHubTour && !wantsIap211)) {
      tourStartedRef.current = false;
      return;
    }
    if (tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_RES_MODEL_TOUR_PARAM);
          params.delete(FOC_RES_213RR_TOUR_PARAM);
          params.delete(FOC_RES_211_TOUR_IAP_PARAM);
          return params;
        },
        { replace: true }
      );
      if (wants211Tour) {
        setSelectedTab('ics211');
        void import('../focResTour/focRes211Tour')
          .then(({ startFocRes211Tour }) => startFocRes211Tour())
          .catch(() => {});
        return;
      }
      if (wants213rrTour) {
        setSelectedTab('request');
        void import('../focResTour/focRes213rrTour')
          .then(({ startFocRes213rrTour }) => startFocRes213rrTour())
          .catch(() => {});
        return;
      }
      void import('../focResTour/focResModelTour')
        .then(({ startFocResModelTour }) => startFocResModelTour())
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    focRequirements,
    isHub,
    setSearchParams,
    wants211Tour,
    wants213rrTour,
    wantsModelTour,
  ]);

  return (
    <div data-tour="resources-workspace">
      <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {assetsOnly ? null : (
            <ToggleGroup
              variant="outline"
              size="sm"
              spacing={0}
              multiple={false}
              value={[selectedTab]}
              onValueChange={(next) => {
                const value = Array.isArray(next) ? next[0] : next;
                if (value === 'resource' || value === 'request') {
                  handleTabChange(value);
                }
              }}
              aria-label="Assets view"
              data-tour="resources-view-toggle"
            >
              <ToggleGroupItem
                value="resource"
                data-tour="resources-tab-assets"
              >
                Assets
              </ToggleGroupItem>
              <ToggleGroupItem
                value="request"
                data-tour="resources-tab-requests"
              >
                Asset Requests
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          {showIcsTabs && !assetsOnly && premiumResourceManagement ? (
            <>
              <Button
                type="button"
                size="sm"
                variant={selectedTab === 'ics211' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('ics211')}
                data-tour="resources-tab-ics211"
              >
                ICS-211
              </Button>
              <Button
                type="button"
                size="sm"
                variant={selectedTab === 'ics238' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('ics238')}
              >
                ICS-238
              </Button>
            </>
          ) : null}
          {selectedTab === 'resource' ? (
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search…"
              aria-label="Search resources"
              className="tw:h-8 tw:w-48"
              data-tour="resources-search"
            />
          ) : null}
        </div>
        {selectedTab === 'resource' ? (
          <div className="tw:flex tw:items-center tw:gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowImportAssets(true)}
              data-tour="resources-import-assets"
            >
              Import Assets
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowAddAsset(!showAddAsset)}
              data-tour="resources-add-asset"
            >
              Add Asset
            </Button>
          </div>
        ) : null}
      </div>
      <AssetImportDialog
        open={showImportAssets}
        onOpenChange={setShowImportAssets}
        onImported={(batchId) =>
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('importBatch', batchId);
              return next;
            },
            { replace: true }
          )
        }
      />
      {selectedTab === 'resource' ? (
        <div data-tour="resources-assets-list">
          <div data-tour="resources-availability" />
          <div data-tour="resources-check-in-out" />
          <div data-tour="resources-tcard" />
          <ResourceView
            showAddAsset={showAddAsset}
            setShowAddAsset={setShowAddAsset}
            handleResourceUpdate={handleResourceUpdate}
            searchTerm={search}
          />
        </div>
      ) : selectedTab === 'ics211' ? (
        <div data-tour="resources-ics211">
          <div data-tour="resources-check-in-status" />
          <Ics211DocumentCard />
        </div>
      ) : selectedTab === 'ics238' ? (
        <Ics238DocumentCard />
      ) : (
        <div data-tour="resources-requests">
          <div data-tour="resources-requests-list" />
          <div data-tour="resources-request-create" />
          <ResourceRequestView hub={isHub} />
        </div>
      )}
      {showModal && (
        <CustomModal
          heading="Cannot View Resource Requests"
          onClose={() => setShowModal(false)}
          show={showModal}
          onConfirm={() => setShowModal(false)}
          saveButton="OK"
        >
          <p>Resource Requests cannot be viewed here</p>
        </CustomModal>
      )}
      <PremiumResourceManagementModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Resources;
